import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatFullName } from 'shared/utils';
import { getContactItemById } from 'shared/store/selectors/contact.selector';
import { ROUTES } from 'shared/constants/routes';

/**
 * AG Grid cell renderer that displays a user name as a link.
 * For org users, links to the contact profile. For external users, links to mailto.
 *
 * Supports three modes:
 * - `userField` param: extracts user object from `data[userField]` (when valueGetter returns something else)
 * - `value` is an object: uses it directly as the user
 * - `value` is a string: uses `data` as the user object, displays `value` as the name
 *
 * @param {import('ag-grid-community').ICellRendererParams & {
 *   className?: string,
 *   userField?: string,
 * }} params
 */
export function UserLinkCell({ value, data, userField, className }) {
  const contactsState = useSelector(({ contacts, usersManagement }) => ({
    contacts,
    usersManagement,
  }));

  const user = userField
    ? data?.[userField]
    : typeof value === 'object' && value !== null
      ? value
      : data;
  if (!user?.id) return value ?? '-';

  const displayName = typeof value === 'string' ? value : formatFullName(user);
  const isInOrg = getContactItemById(contactsState, user.id);

  const commonProps = {
    className,
    onClick: e => e.stopPropagation(),
    onKeyDown: e => e.stopPropagation(),
  };

  if (!isInOrg) {
    return (
      <a {...commonProps} href={`mailto:${user.email ?? ''}`}>
        {displayName || '-'}
      </a>
    );
  }

  return (
    <Link {...commonProps} to={ROUTES.CONTACTS_PREVIEW.replace(':id', user.id)}>
      {displayName || '-'}
    </Link>
  );
}
